// ─────────────────────────────────────────────────────────────
// ZIFEK — Blocs de la page d'accueil publique
// Le marchand choisit les blocs affichés ET leur ordre depuis le
// dashboard. La config est stockée dans `merchant_home_config.blocks`
// sous forme de tableau [{ key, enabled }] (l'ordre du tableau = ordre
// d'affichage sur la page d'accueil).
//
// Le champ `scope` indique à quel type de commerce un bloc s'applique :
//   • 'products' → comptes Boutique
//   • 'services' → comptes Services
//   • 'both'     → transverse (ex. Bannière), affiché partout
// Le type de compte « Mixte » cumule les blocs produits ET services.
// ─────────────────────────────────────────────────────────────

export type HomeBlockScope = 'products' | 'services' | 'both' | 'restaurant';

/** Portée demandée pour construire une page d'accueil. */
export type HomeScopeQuery = 'products' | 'services' | 'restaurant' | 'all';

export interface HomeBlockDef {
  key: string;
  label: string;
  description: string;
  icon: string;
  /** Types de commerce pour lesquels le bloc est pertinent. */
  scope: HomeBlockScope;
  /** Activé par défaut quand le marchand n'a rien configuré. */
  defaultEnabled: boolean;
  /** Libellé alternatif pour les commerces de restauration (facultatif). */
  restaurantLabel?: string;
}

export interface HomeBlockConfig {
  key: string;
  enabled: boolean;
}

// L'ordre de ce tableau sert d'ordre par défaut.
// Pour un compte Mixte, l'ordre effectif devient :
//   Bannière → Services en vedette → Nos services → Produits en vedette
//   → Nos produits → Portfolio → Témoignages → Équipe → Partenaires
export const HOME_BLOCKS: HomeBlockDef[] = [
  {
    key: 'banner',
    label: 'Bannière',
    description: "Slider d'images créé dans « Bannières d'accueil ».",
    icon: 'ri-gallery-view-2',
    scope: 'both',
    defaultEnabled: true,
  },
  {
    key: 'promo',
    label: 'Promo du moment',
    description: 'Le plat / produit choisi comme promo du moment depuis le dashboard Restaurant.',
    icon: 'ri-fire-line',
    scope: 'restaurant',
    defaultEnabled: true,
  },
  {
    key: 'featured_services',
    label: 'Services en vedette',
    description: 'Services marqués d’une étoile depuis le dashboard Services.',
    icon: 'ri-star-line',
    scope: 'services',
    defaultEnabled: true,
  },
  {
    key: 'services',
    label: 'Nos services (grille)',
    description: 'Grille de services avec un bouton « Voir plus ».',
    icon: 'ri-service-line',
    scope: 'services',
    defaultEnabled: true,
  },
  {
    key: 'featured_products',
    label: 'Produits en vedette',
    description: 'Produits marqués d’une étoile depuis le dashboard Produits.',
    icon: 'ri-star-line',
    scope: 'products',
    defaultEnabled: true,
  },
  {
    key: 'products',
    label: 'Nos produits (grille 3×3)',
    restaurantLabel: 'Notre menu',
    description: 'Grille de produits avec un bouton « Voir plus ».',
    icon: 'ri-shopping-bag-3-line',
    scope: 'products',
    defaultEnabled: true,
  },
  {
    key: 'cta',
    label: 'Appel à l’action',
    description: 'Une bannière avec un bouton (formulaire, contact, réservation, lien externe…).',
    icon: 'ri-megaphone-line',
    scope: 'both',
    defaultEnabled: false,
  },
  {
    key: 'booking_calendar',
    label: 'Calendrier de réservation',
    description: 'Affiche le calendrier par défaut (service choisi ci-dessous) sur la page d’accueil.',
    icon: 'ri-calendar-check-line',
    scope: 'both',
    defaultEnabled: false,
  },
  {
    key: 'portfolio',
    label: 'Nos réalisations',
    description: 'Vos projets déjà réalisés (grille 3×3).',
    icon: 'ri-briefcase-line',
    scope: 'services',
    defaultEnabled: true,
  },
  {
    key: 'testimonials',
    label: 'Nos témoignages',
    description: 'Avis de vos clients.',
    icon: 'ri-chat-quote-line',
    scope: 'services',
    defaultEnabled: true,
  },
  {
    key: 'team',
    label: 'Notre équipe',
    description: 'Les membres de votre équipe (nom, rôle, photo).',
    icon: 'ri-user-star-line',
    scope: 'services',
    defaultEnabled: true,
  },
  {
    key: 'partners',
    label: 'Nos partenaires',
    description: 'Vos partenaires (nom, description, image).',
    icon: 'ri-team-line',
    scope: 'services',
    defaultEnabled: true,
  },
  {
    key: 'about',
    label: 'À propos / présentation',
    description: 'Un texte de présentation de votre activité, configurable ci-dessous.',
    icon: 'ri-file-text-line',
    scope: 'both',
    defaultEnabled: true,
  },
];

const BLOCK_MAP = new Map(HOME_BLOCKS.map((b) => [b.key, b]));

export function getBlockDef(key: string): HomeBlockDef | undefined {
  return BLOCK_MAP.get(key);
}

/** Ordre par défaut, blocs activés. */
export function defaultBlockConfig(): HomeBlockConfig[] {
  return HOME_BLOCKS.map((b) => ({ key: b.key, enabled: b.defaultEnabled }));
}

/**
 * Normalise une config brute lue en base :
 * - conserve l'ordre enregistré pour les clés connues ;
 * - ignore les clés inconnues ;
 * - ajoute à la fin tout bloc défini mais absent de la config.
 */
export function normalizeBlockConfig(raw: unknown): HomeBlockConfig[] {
  const seen = new Set<string>();
  const result: HomeBlockConfig[] = [];

  if (Array.isArray(raw)) {
    raw.forEach((entry) => {
      const obj = entry as { key?: unknown; enabled?: unknown };
      const key = typeof obj?.key === 'string' ? obj.key : '';
      if (!key || seen.has(key) || !BLOCK_MAP.has(key)) return;
      seen.add(key);
      result.push({ key, enabled: obj.enabled !== false });
    });
  }

  HOME_BLOCKS.forEach((b) => {
    if (!seen.has(b.key)) result.push({ key: b.key, enabled: b.defaultEnabled });
  });

  return result;
}

/** Un bloc est-il pertinent pour une portée demandée ? */
function blockMatchesScope(def: HomeBlockDef, scope: HomeScopeQuery): boolean {
  if (scope === 'all') return true;
  return def.scope === scope || def.scope === 'both';
}

/** Libellé d'un bloc, adapté aux commerces de restauration. */
export function blockLabel(def: HomeBlockDef, restaurant: boolean): string {
  return restaurant && def.restaurantLabel ? def.restaurantLabel : def.label;
}

/**
 * Ordre des blocs de la page d'accueil « Restauration ». On respecte
 * l'ordre enregistré par le marchand, en ne gardant que les blocs
 * pertinents : Bannière → Promo du moment → Notre menu → À propos.
 */
export function restaurantBlockOrder(blocks: HomeBlockConfig[]): string[] {
  const allowed = new Set(['banner', 'promo', 'products', 'about']);
  return blocks.filter((b) => allowed.has(b.key) && b.enabled).map((b) => b.key);
}

/** Blocs pertinents pour un type de commerce. */
export function blocksForScope(scope: HomeScopeQuery): HomeBlockDef[] {
  return HOME_BLOCKS.filter((b) => blockMatchesScope(b, scope));
}

/** Un bloc est-il activé dans la config ? (activé par défaut si absent). */
export function isBlockEnabled(blocks: HomeBlockConfig[], key: string): boolean {
  const found = blocks.find((b) => b.key === key);
  return found ? found.enabled : true;
}

/**
 * La liste ordonnée des blocs ACTIVÉS et pertinents pour la portée demandée.
 * C'est exactement l'ordre dans lequel construire la page d'accueil.
 */
export function orderedEnabledBlocks(blocks: HomeBlockConfig[], scope: HomeScopeQuery): string[] {
  return blocks
    .filter((b) => {
      const def = BLOCK_MAP.get(b.key);
      return Boolean(def) && blockMatchesScope(def!, scope) && b.enabled;
    })
    .map((b) => b.key);
}

export interface MerchantHomeConfig {
  blocks: HomeBlockConfig[];
  defaultBookingServiceId: number | null;
  /** Nombre de colonnes de la grille « Nos services » (2, 3 ou 4). */
  servicesColumns: number;
  /** Nombre de colonnes de la grille « Nos produits » (2, 3 ou 4). */
  productsColumns: number;
  /** Titre du bloc « À propos / présentation » (facultatif). */
  aboutTitle: string;
  /** Texte du bloc « À propos / présentation » (facultatif). */
  aboutContent: string;
  /** Bloc appel à l’action : titre, texte, libellé du bouton et lien (facultatifs). */
  ctaTitle: string;
  ctaText: string;
  ctaButtonLabel: string;
  ctaUrl: string;
  /** Produit choisi comme « Promo du moment » (restauration). */
  promoProductId: number | null;
  /** Horaires d'ouverture (restauration). null = non configuré. */
  openingHours: unknown | null;
}

/** Colonnes proposées pour les grilles « Nos services » / « Nos produits ». */
export const SERVICE_COLUMN_OPTIONS = [2, 3, 4] as const;
export const PRODUCT_COLUMN_OPTIONS = [2, 3, 4] as const;
export const DEFAULT_SERVICE_COLUMNS = 3;
export const DEFAULT_PRODUCT_COLUMNS = 3;

/** Normalise le nombre de colonnes (retombe sur 3 si valeur inattendue). */
export function normalizeColumnCount(value: unknown, fallback: number): number {
  const n = Number(value);
  return [2, 3, 4].includes(n) ? n : fallback;
}

export function normalizeServicesColumns(value: unknown): number {
  return normalizeColumnCount(value, DEFAULT_SERVICE_COLUMNS);
}

export function normalizeProductsColumns(value: unknown): number {
  return normalizeColumnCount(value, DEFAULT_PRODUCT_COLUMNS);
}

/** Classe Tailwind de la grille selon le nombre de colonnes.
 *  Règle d'affichage : 3 par ligne sur ordinateur, 2 par ligne sur mobile
 *  (le format « 3×3×3 » demandé côté desktop, « 2×2×2 » côté mobile). */
export function gridClass(columns: number, fallback = 3): string {
  switch (normalizeColumnCount(columns, fallback)) {
    case 2:
      return 'grid grid-cols-2 gap-3 md:gap-5';
    case 4:
      return 'grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5';
    default:
      return 'grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5';
  }
}

/** Classe Tailwind de la grille « Nos services ». */
export function servicesGridClass(columns: number): string {
  return gridClass(columns, DEFAULT_SERVICE_COLUMNS);
}

/** Classe Tailwind de la grille « Nos produits ». */
export function productsGridClass(columns: number): string {
  return gridClass(columns, DEFAULT_PRODUCT_COLUMNS);
}

export const EMPTY_HOME_CONFIG: MerchantHomeConfig = {
  blocks: defaultBlockConfig(),
  defaultBookingServiceId: null,
  servicesColumns: DEFAULT_SERVICE_COLUMNS,
  productsColumns: DEFAULT_PRODUCT_COLUMNS,
  aboutTitle: '',
  aboutContent: '',
  ctaTitle: '',
  ctaText: '',
  ctaButtonLabel: '',
  ctaUrl: '',
  promoProductId: null,
  openingHours: null,
};